import { checkPrerequisites } from '../functions/checkPrerequisites.js'

const SERVICE_QUESTIONS = {
  whitening: [
    { key: 'lastCleaningDate', type: 'date', label: '{{ lastCleaningQuestion | polyglot }}' }
  ],
  implant: [
    { key: 'hasCtScan', type: 'checkbox', label: '{{ hasCtScanQuestion | polyglot }}' },
    { key: 'hasConsultation', type: 'checkbox', label: '{{ hasConsultationQuestion | polyglot }}' }
  ],
  orthodonticAdjustment: [
    { key: 'hasBraces', type: 'checkbox', label: '{{ hasBracesQuestion | polyglot }}' }
  ],
  rootCanal: [
    { key: 'hasRecentXray', type: 'checkbox', label: '{{ hasRecentXrayQuestion | polyglot }}' }
  ]
}

export const ServicePreCheck = {
  flow: 'y',
  gap: 'A',
  padding: 'B',
  round: 'A',
  background: 'inkMid',
  border: '1px solid inkBorder',
  width: '100%',

  PreCheckTitle: {
    tag: 'h2',
    text: '{{ preCheckTitle | polyglot }}',
    fontSize: 'B',
    fontWeight: '700',
    color: 'inkText'
  },

  PreCheckSubtitle: {
    text: '{{ preCheckSubtitle | polyglot }}',
    fontSize: 'Z',
    color: 'inkMuted'
  },

  QuestionList: {
    flow: 'y',
    gap: 'Z',
    children: (el, s) => {
      const serviceId = s.root.selectedServiceId
      return SERVICE_QUESTIONS[serviceId] || []
    },
    childrenAs: 'state',
    childExtends: 'PreCheckQuestion'
  },

  PreCheckQuestion: {
    flow: 'x',
    align: 'center start',
    gap: 'Z',
    padding: 'Z A',
    round: 'Y',
    background: 'inkSurface',
    border: '1px solid inkBorder',

    QuestionInput: {
      extends: 'Input',
      tag: 'input',
      type: (el, s) => s.type || 'text',
      padding: 'Y Z',
      round: 'Y',
      background: 'ink',
      color: 'inkText',
      border: '1px solid inkBorder',
      fontSize: 'Z',
      width: (el, s) => s.type === 'checkbox' ? 'A' : '100%',
      height: (el, s) => s.type === 'checkbox' ? 'A' : 'auto',
      cursor: 'pointer',
      onChange: (e, el, s) => {
        const answers = s.root.preCheckAnswers || {}
        const val = s.type === 'checkbox' ? el.node.checked : el.node.value
        answers[s.key] = val
        const result = checkPrerequisites(s.root.selectedServiceId, answers)
        s.root.update({ preCheckAnswers: answers, preCheckPassed: result.passed, preCheckResult: result })
      }
    },

    QuestionLabel: {
      text: (el, s) => s.label,
      fontSize: 'Z',
      color: 'inkText',
      flex: '1'
    }
  },

  PreCheckWarning: {
    flow: 'y',
    gap: 'Y',
    padding: 'A',
    round: 'Y',
    background: 'orange.1',
    border: '1px solid orange.3',
    show: (el, s) => s.root.preCheckResult && !s.root.preCheckResult.passed && s.root.preCheckResult.missing.length > 0,

    WarningIcon: {
      extends: 'Icon',
      name: 'alertTriangle',
      fontSize: 'A',
      color: 'orange'
    },

    WarningText: {
      text: '{{ prerequisiteWarning | polyglot }}',
      fontSize: 'Z',
      color: 'orange',
      fontWeight: '600'
    },

    MissingList: {
      flow: 'y',
      gap: 'X',
      children: (el, s) => (s.root.preCheckResult && s.root.preCheckResult.missing || []).map(m => ({ name: m })),
      childrenAs: 'state',
      childExtends: 'MissingItem'
    },

    MissingItem: {
      text: (el, s) => '• ' + s.name,
      fontSize: 'Y',
      color: 'orange.8'
    },

    BookPrereqBtn: {
      extends: 'Button',
      text: '{{ bookPrerequisiteFirst | polyglot }}',
      background: 'orange',
      color: 'white',
      fontWeight: '600',
      padding: 'Y A',
      round: 'Y',
      fontSize: 'Y',
      cursor: 'pointer',
      border: 'none',
      marginTop: 'Y',
      onClick: (e, el, s) => {
        const missing = s.root.preCheckResult && s.root.preCheckResult.missing
        if (missing && missing[0]) {
          s.root.update({ selectedServiceId: missing[0], activeBookingStep: 2, preCheckAnswers: {}, preCheckPassed: false, preCheckResult: null })
        }
      }
    }
  },

  PreCheckPassedMsg: {
    flow: 'x',
    align: 'center center',
    gap: 'Y',
    padding: 'A',
    round: 'Y',
    background: 'teal.1',
    border: '1px solid teal.3',
    show: (el, s) => s.root.preCheckPassed === true,

    PassedIcon: {
      extends: 'Icon',
      name: 'checkCircle',
      fontSize: 'A',
      color: 'teal'
    },

    PassedText: {
      text: '{{ preCheckPassed | polyglot }}',
      fontSize: 'Z',
      color: 'teal',
      fontWeight: '600'
    }
  },

  ContinueBtn: {
    extends: 'Button',
    text: '{{ continueToScheduling | polyglot }}',
    show: (el, s) => s.root.preCheckPassed === true,
    background: '#0D1117',
    color: '#FFFFFF',
    fontWeight: '600',
    padding: 'Z B',
    round: 'B',
    fontSize: 'Z',
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    textAlign: 'center',
    marginTop: 'Y',
    onClick: (e, el, s) => s.root.update({ activeBookingStep: 4 })
  },

  NoPrereqMsg: {
    flow: 'y',
    align: 'center center',
    gap: 'Y',
    padding: 'A',
    show: (el, s) => {
      const serviceId = s.root.selectedServiceId
      return !SERVICE_QUESTIONS[serviceId]
    },

    NoPrereqText: {
      text: '{{ preCheckPassed | polyglot }}',
      fontSize: 'Z',
      color: 'teal',
      fontWeight: '600'
    },

    AutoContinueBtn: {
      extends: 'Button',
      text: '{{ continueToScheduling | polyglot }}',
      background: '#0D1117',
      color: '#FFFFFF',
      fontWeight: '600',
      padding: 'Z B',
      round: 'B',
      fontSize: 'Z',
      cursor: 'pointer',
      border: 'none',
      width: '100%',
      textAlign: 'center',
      onClick: (e, el, s) => s.root.update({ activeBookingStep: 4 })
    }
  }
}
